import {
  Checkbox,
  MenuItem,
  Select,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import React from "react";
import { useSelector } from "react-redux";
import { IStoreState } from "../../../redux/initialStoreState";
import { useDispatchWrapper } from "../../../hooks";
import { useBillingCompanyContext } from "../../../providers";
import {
  ICampaignRule,
  ICampaignRuleTemplate,
  IMarketCampaignAudience,
} from "../../../redux/marketCampaign/marketCampaign.types";
import { fetchCampaignRuleTemplatesAsync } from "../../../redux/marketCampaign/marketCampaignActions";
import { PageContainer } from "../../../components/container/PageContainer";
import { PageTitleBar } from "../../../components/PageTitleBar/PageTitleBar";
import { CustomCheckbox } from "../../../components/formsComponents";
import { isEmpty } from "lodash";
import { MuiNumberField } from "../../../components/formsComponents/MuiNumberField";
import { LoadState } from "../../../constants/enums";

export type { ICampaignRule } from "../../../redux/marketCampaign/marketCampaign.types";

const EMPTY_CAMPAIGN_RULE: ICampaignRule = {
  event_type: [],
  status: [],
  duration_type: "",
  number_count: 1,
  duration: "",
};

export const MarketingCampaignTemplateRuleList: React.FC<{
  values: IMarketCampaignAudience;
  setFieldValue: (field: string, value: any) => void;
  seValues: (values: IMarketCampaignAudience) => void;
  marketCampaignId?: string;
  isDisabled?: boolean;
}> = (props) => {
  const { list, loading } = useSelector(
    (storeState: IStoreState) =>
      storeState.marketCampaign.marketCampaignRuleTemplate,
  );

  const dispatch = useDispatchWrapper();
  const companyOutlet = useBillingCompanyContext();

  // Fetch templates on mount
  React.useEffect(() => {
    dispatch(
      fetchCampaignRuleTemplatesAsync(
        companyOutlet.comapny.company_outlet_uuid,
      ),
    );
  }, []);

  // Derive selected UUID directly from props (single source of truth)
  const selectedUuid = props.values.market_campaign_rule_template_uuid || "";

  // Derive rows: merge saved campaign_rule onto the matching template row
  const rows = React.useMemo(() => {
    return (list || []).map((row) => {
      if (row.market_campaign_rule_template_uuid === selectedUuid && props.values.campaign_rule) {
        return { ...row, campaign_rule: props.values.campaign_rule };
      }
      return row;
    });
  }, [list, selectedUuid, props.values.campaign_rule]);

  // Handle row selection toggle
  const handleToggleRow = React.useCallback(
    (template: ICampaignRuleTemplate) => {
      const isAlreadySelected =
        selectedUuid === template.market_campaign_rule_template_uuid;

      if (isAlreadySelected) {
        // Deselect: clear form fields
        props.seValues({
          ...props.values,
          market_campaign_rule_template_uuid: "",
          market_campaign_rule_template_name: "",
          campaign_rule: { ...EMPTY_CAMPAIGN_RULE },
        });
      } else {
        // Select new row
        props.seValues({
          ...props.values,
          market_campaign_rule_template_uuid:
            template.market_campaign_rule_template_uuid,
          market_campaign_rule_template_name: template.template_name,
          campaign_rule: isEmpty(template.variables_details)
            ? null
            : { ...EMPTY_CAMPAIGN_RULE },
        });
      }
    },
    [selectedUuid, props.values, props.seValues],
  );

  // Handle variable field change
  const handleVariableChange = React.useCallback(
    (variableName: string, newValue: any) => {
      props.setFieldValue("campaign_rule", {
        ...(props.values.campaign_rule || EMPTY_CAMPAIGN_RULE),
        [variableName]: newValue,
      });
    },
    [props.values.campaign_rule, props.setFieldValue],
  );

  const isLoading = loading === LoadState.InProgress || loading === LoadState.NotLoaded;

  return (
    <PageContainer title="View Market Campaign" description="this is innerpage">
      <PageTitleBar heading="" />

      {isLoading ? (
        <RuleTemplateSkeleton />
      ) : (
      <TableContainer sx={{ mb: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell width={60}></TableCell>
              <TableCell sx={{ fontSize: "1.1rem", fontWeight: 600 }}>
                Template
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row, index) => {
              const isRowSelected =
                selectedUuid === row.market_campaign_rule_template_uuid;
              const isDisabledByRule =
                !Boolean(row.is_enable) ||
                !row.market_campaign_rule_template_uuid;
              const campaignRule = row.campaign_rule || {};
              const variables = row.variables_details;
              const templateParts = row.template?.split(/(@@\w+)/g);

              return (
                <TableRow key={row.market_campaign_rule_template_uuid || index}>
                  <TableCell>
                    <CustomCheckbox
                      disabled={props.isDisabled || isDisabledByRule}
                      checked={isRowSelected}
                      onChange={() => handleToggleRow(row)}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body1"
                      fontWeight={600}
                      fontSize="0.95rem"
                    >
                      {row.template_name}
                    </Typography>

                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 4,
                        alignItems: "center",
                      }}
                    >
                      {templateParts?.map((part, partIndex) => {
                        if (!part?.startsWith("@@")) {
                          return <span key={partIndex}>{part}</span>;
                        }

                        const variableName = part.replace("@@", "");
                        const variableConfig = variables?.find(
                          (v) => v.variable === variableName,
                        );

                        if (!variableConfig) return <span key={partIndex}>{part}</span>;

                        const fieldValue = (campaignRule as Record<string, any>)[variableName];
                        const isFieldDisabled = !isRowSelected || !!props.isDisabled;

                        const commonSx = { minWidth: 120, mx: 0.5 };

                        return (
                          <TemplateVariableField
                            key={partIndex}
                            type={variableConfig.type}
                            options={variableConfig.options}
                            value={fieldValue}
                            variableName={variableName}
                            disabled={isFieldDisabled}
                            isRowSelected={isRowSelected}
                            sx={commonSx}
                            onChange={(newValue) =>
                              handleVariableChange(variableName, newValue)
                            }
                          />
                        );
                      })}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      )}
    </PageContainer>
  );
};

// --- Skeleton loader for the rule template table ---

const SKELETON_ROWS = 4;

const RuleTemplateSkeleton: React.FC = () => (
  <TableContainer sx={{ mb: 2 }}>
    <Table size="small">
      <TableHead>
        <TableRow>
          <TableCell width={60}></TableCell>
          <TableCell sx={{ fontSize: "1.1rem", fontWeight: 600 }}>
            Template
          </TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {Array.from({ length: SKELETON_ROWS }).map((_, index) => (
          <TableRow key={index}>
            <TableCell>
              <Skeleton variant="rectangular" width={24} height={24} sx={{ borderRadius: 0.5 }} />
            </TableCell>
            <TableCell>
              <Skeleton variant="text" width="35%" height={22} sx={{ mb: 0.5 }} />
              <Skeleton variant="text" width="70%" height={18} />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  </TableContainer>
);

// --- Internal sub-component for rendering variable fields ---

interface TemplateVariableFieldProps {
  type: string;
  options?: string[];
  value: any;
  variableName: string;
  disabled: boolean;
  isRowSelected: boolean;
  sx: Record<string, any>;
  onChange: (newValue: any) => void;
}

const TemplateVariableField = React.memo<TemplateVariableFieldProps>(
  ({ type, options, value, variableName, disabled, isRowSelected, sx, onChange }) => {
    switch (type) {
      case "multiselect_dropdown": {
        if (!isRowSelected) {
          const display = Array.isArray(value) && value.length > 0
            ? value.join(", ")
            : `(e.g., ${variableName === "event_type" ? "Event Name" : "Status"})`;
          return <span>{display}</span>;
        }
        return (
          <Select
            size="small"
            variant="outlined"
            multiple
            disabled={disabled}
            value={value || []}
            onChange={(e) =>
              onChange(
                (e.target.value as string[]).map((v) => v.toUpperCase()),
              )
            }
            renderValue={(selected) => (selected as string[]).join(", ")}
            sx={sx}
          >
            {(options || []).map((option) => (
              <MenuItem key={option} value={option.toUpperCase()}>
                <Checkbox
                  disabled={disabled}
                  checked={(value || []).includes(option.toUpperCase())}
                />
                {option}
              </MenuItem>
            ))}
          </Select>
        );
      }

      case "dropdown": {
        if (!isRowSelected) {
          const display = value ||
            `(e.g., ${variableName === "duration_type" ? "Next/Previous" : "Duration"})`;
          return <span>{display}</span>;
        }
        return (
          <Select
            size="small"
            variant="outlined"
            disabled={disabled}
            value={value || ""}
            onChange={(e) => onChange((e.target.value as string).toUpperCase())}
            sx={sx}
          >
            {(options || []).map((option) => (
              <MenuItem key={option} value={option.toUpperCase()}>
                {option}
              </MenuItem>
            ))}
          </Select>
        );
      }

      case "number": {
        if (!isRowSelected) {
          return <span>(e.g. Number)</span>;
        }
        return (
          <MuiNumberField
            disabled={disabled}
            label=""
            name={variableName}
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            sx={sx}
          />
        );
      }

      default:
        return <span>{`@@${variableName}`}</span>;
    }
  },
);

TemplateVariableField.displayName = "TemplateVariableField";
